import React, { useLayoutEffect } from 'react';
import { ArrowLeft, LogOut } from 'lucide-react';
import { ALTO_BARRA_SUPLANTACION_PX, TOP_BARRA_SUPLANTACION, publicarAltoBarraSuplantacion } from './avisos-superiores';

interface BarraDeSuplantacionProps {
  /** Nombre de la persona cuyo espacio se está viendo. */
  nombre: string;
  /** «Volver a Super Admin». */
  onVolver: () => void;
}

/**
 * GH#2768 — barra fija «Estás viendo el workspace de … / Volver a Super Admin» (salía de MainLayout).
 *
 * Es un aviso fijo más, así que sigue la regla de avisos-superiores.ts: va DEBAJO del aviso amarillo de modo demo (nunca en `top-0`, donde
 * el amarillo, z-120, la tapaba y el clic en «Volver» no llegaba), tiene alto FIJO (el texto se corta con «…» en vez de partirse en dos líneas) y
 * publica ese alto en una variable CSS que `desplazamientoPorAvisos` suma, de modo que la cabecera, las barras laterales y el contenido bajan
 * debajo de ella sin que nadie escriba 40 o 44 a mano.
 *
 * Contraste: blanco sobre naranja-700 = 5,2:1 (el naranja-600 de antes daba 3,6:1, por debajo de 4,5:1 para texto chico).
 * Objetivo táctil: 44 px de alto en pantallas angostas (WCAG 2.5.8 / regla de 44pt), 32 px con puntero fino.
 */
export const BarraDeSuplantacion: React.FC<BarraDeSuplantacionProps> = ({ nombre, onVolver }) => {
  useLayoutEffect(() => {
    publicarAltoBarraSuplantacion(ALTO_BARRA_SUPLANTACION_PX);
    return () => publicarAltoBarraSuplantacion(0);
  }, []);

  return (
    <div
      className="fixed left-0 right-0 z-[110] flex items-center justify-between gap-3 overflow-hidden bg-orange-700 px-4 text-white"
      style={{ top: TOP_BARRA_SUPLANTACION, height: ALTO_BARRA_SUPLANTACION_PX }}
    >
      <div className="flex min-w-0 items-center space-x-3">
        <div className="flex-shrink-0 rounded-lg bg-white/20 p-1.5">
          <LogOut className="h-4 w-4" />
        </div>
        <p className="truncate text-sm font-medium">
          Estás viendo el workspace de <span className="font-bold">{nombre}</span>
        </p>
      </div>
      <button
        type="button"
        onClick={onVolver}
        className="flex h-11 flex-shrink-0 items-center space-x-2 rounded-full bg-white px-4 text-xs font-bold text-orange-700 transition-colors hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:h-8"
      >
        <ArrowLeft className="h-3 w-3" />
        <span>Volver a Super Admin</span>
      </button>
    </div>
  );
};

export default BarraDeSuplantacion;
